/**
 * 知识库服务
 * 负责：
 * 1. 读取知识库
 * 2. 添加新条目
 * 3. 过滤"老友"条目
 * 4. 保存到 localStorage（暂时方案，以后同步到在线表格）
 */

import knowledgeBaseData from '../data/knowledgeBase.json';
import chatService from './chatService';

class KnowledgeService {
  constructor() {
    this.knowledgeBase = [];
    this.loadKnowledgeBase();
  }

  /**
   * 加载知识库
   * 优先从 localStorage 读取，否则从 JSON 文件读取
   */
  loadKnowledgeBase() {
    try {
      const localData = localStorage.getItem('talktastic_knowledge_base');
      if (localData) {
        const parsed = JSON.parse(localData);
        this.knowledgeBase = parsed.knowledgeBase || parsed;
      } else {
        this.knowledgeBase = knowledgeBaseData.knowledgeBase || [];
      }
    } catch (error) {
      console.error('加载知识库失败:', error);
      this.knowledgeBase = knowledgeBaseData.knowledgeBase || [];
    }
  }

  /**
   * 保存知识库到 localStorage
   */
  saveKnowledgeBase() {
    try {
      localStorage.setItem('talktastic_knowledge_base', JSON.stringify({
        knowledgeBase: this.knowledgeBase,
        total: this.knowledgeBase.length,
        lastUpdated: new Date().toISOString(),
      }));
    } catch (error) {
      console.error('保存知识库失败:', error);
    }
  }

  /**
   * 获取所有知识库条目
   * @returns {Array} 知识库条目
   */
  getAll() {
    return this.knowledgeBase;
  }

  /**
   * 获取可搜索的知识库（排除"老友"）
   * @returns {Array} 过滤后的知识库
   */
  getSearchable() {
    return this.knowledgeBase.filter(item => item.memoryLevel !== '老友');
  }

  /**
   * 添加新条目到知识库
   * @param {string} content - 英语表达
   * @param {string} meaning - 中文含义（可选，自动获取）
   * @returns {Promise<Object>} 新添加的条目
   */
  async addItem(content, meaning = null) {
    // 检查是否已存在
    const exists = this.knowledgeBase.find(
      item => item.content.toLowerCase().trim() === content.toLowerCase().trim()
    );
    if (exists) {
      return { success: false, message: '该表达已存在', item: exists };
    }

    // 获取分类和含义
    const [category, autoMeaning] = await Promise.all([
      chatService.classifyKnowledge(content),
      meaning ? Promise.resolve(meaning) : chatService.getMeaning(content),
    ]);

    const newItem = {
      id: `knowledge_${Date.now()}`,
      content: content.trim(),
      meaning: autoMeaning,
      category,
      memoryLevel: '初见',
      usageCount: 0,
      importDate: new Date().toISOString().slice(0, 10),
      status: 'learning',
      createdDate: new Date().toISOString(),
    };

    this.knowledgeBase.push(newItem);
    this.saveKnowledgeBase();

    return { success: true, item: newItem };
  }

  /**
   * 更新条目的记忆等级
   * @param {string} id - 条目 ID
   * @param {string} level - 新的记忆等级
   */
  updateMemoryLevel(id, level) {
    const item = this.knowledgeBase.find(item => item.id === id);
    if (item) {
      item.memoryLevel = level;
      this.saveKnowledgeBase();
    }
  }

  /**
   * 增加使用次数
   * @param {string} id - 条目 ID
   */
  incrementUsage(id) {
    const item = this.knowledgeBase.find(item => item.id === id);
    if (item) {
      item.usageCount = (item.usageCount || 0) + 1;
      this.saveKnowledgeBase();
    }
  }

  /**
   * 搜索知识库（简单文本匹配，暂时方案）
   * 以后替换为向量搜索
   * @param {string} query - 搜索词
   * @returns {Array} 匹配的条目
   */
  search(query) {
    const searchable = this.getSearchable();
    const lowerQuery = query.toLowerCase();
    
    return searchable.filter(item => 
      item.content.toLowerCase().includes(lowerQuery) ||
      item.meaning.toLowerCase().includes(lowerQuery)
    ).slice(0, 10);
  }

  /**
   * 获取统计信息
   */
  getStats() {
    const stats = {
      total: this.knowledgeBase.length,
      byCategory: {},
      byMemoryLevel: {},
    };

    this.knowledgeBase.forEach(item => {
      stats.byCategory[item.category] = (stats.byCategory[item.category] || 0) + 1;
      stats.byMemoryLevel[item.memoryLevel] = (stats.byMemoryLevel[item.memoryLevel] || 0) + 1;
    });

    return stats;
  }
}

// 单例
const knowledgeService = new KnowledgeService();
export default knowledgeService;




